import { TNotificationForm } from '@/validation/notification';
import { api, TCustomAxiosPaginationResponse, TCustomAxiosResponse } from '..';
import { TNotification, TNotificationApi, TNotificationFilterOption } from '@/types/Notification.types';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import { label4BEMapper } from '@/utils/mappers/label';
import { apiPaths } from '@/services/api/paths';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

export const getNotifications = async (page = 1, limit = 10): Promise<TCustomAxiosResponse<TNotification[]>> => {
  const { data } = await api.get<TCustomAxiosResponse<TNotification[]>>(apiPaths.notifications.root, {
    params: { page, limit },
  });

  return data;
};

export const getUnreadNotificationsCount = async (): Promise<number> => {
  const { data } = await api.get<{ count: number }>(apiPaths.notifications.unreadCount);

  return data.count;
};

export const getNotificationById = async (notificationId: string): Promise<TNotification[]> => {
  const { data } = await api.get<TCustomAxiosResponse<TNotification[]>>(apiPaths.notifications.byId(notificationId));
  const notification = data.data;

  return notification;
};

export const markAllNotificationsAsRead = async (): Promise<string> => {
  const { data } = await api.post(apiPaths.notifications.markAllRead);

  const result = data.data;

  return result;
};

export const toggleNotificationReadStatus = async (notificationId: string, isRead: boolean): Promise<string> => {
  const { data } = await api.patch(apiPaths.notifications.readStatus(notificationId), { isRead });

  const result = data.data;

  return result;
};

export const toggleMultipleNotificationsReadStatus = async (notificationIds: string[]): Promise<string> => {
  const { data } = await api.post(apiPaths.notifications.markMultipleRead, { notificationIds });

  const result = data.data;

  return result;
};

export const postNotificationAdminBulk = async ({
  form,
  options,
}: {
  form: TNotificationForm;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: Record<string, any>;
}) => {
  const { data } = await api.post(apiPaths.notifications.adminBulk, {
    ...form,
    ...options,
  });

  return data;
};

export const getNotificationsAllAdmin = async (
  page: number,
  filterOption: TNotificationFilterOption,
  filterText: string,
  sortingColumn?: string,
  sortingDirection?: string,
): Promise<TCustomAxiosPaginationResponse<TNotificationApi[]>> => {
  const params: { [key: string]: string | number | boolean } = {
    limit: SUPER_ADMIN_PAGE_SIZE,
    page,
  };

  if (filterText && filterText?.length >= MIN_CHARS_TO_DO_SEARCH) {
    const isReadFilter = filterOption === 'Is Read';
    if (isReadFilter) {
      params['isRead'] = filterText === 'Yes' ? true : false;
    }

    if (!isReadFilter) {
      const filterOptionBE = label4BEMapper(filterOption);
      params[filterOptionBE] = filterText;
    }
  }

  params['sortBy'] = label4BEMapper(sortingColumn ?? 'createdAt');
  params['sortOrder'] = sortingDirection ?? 'desc';

  const { data } = await api.get<TCustomAxiosPaginationResponse<TNotificationApi[]>>(apiPaths.notifications.adminAll, {
    params,
  });

  return data;
};

export const getNotificationByIdSuperAdmin = async (notificationId: string): Promise<TNotificationApi> => {
  const { data } = await api.get<TCustomAxiosResponse<TNotificationApi>>(apiPaths.notifications.adminById(notificationId));

  return data.data;
};
